import { CheckCircle, Circle, Page, WarningTriangle } from 'iconoir-react'
import moment from 'moment'
import Spinner from 'app/components/Spinner'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import {
  patientDetails,
  patientName,
  paperworkState,
  parseSentPacket,
  parseSubmittedAnswers,
  type PatientChoice,
  type PaperworkState,
} from 'app/lib/forms/paperworkRecord'
import { closeModal, showModal } from 'app/lib/modal'
import { SendPaperworkDialog } from './SendPaperworkDialog'
import { PaperworkAnswers } from './PaperworkAnswers'
import type { PaperworkRecord, UseContactDocumentsReturn } from './useContactDocuments'

const STATE_BADGES: Record<PaperworkState, { label: string; color: BadgeColor }> = {
  sent: { label: `Sent`, color: `blue` },
  opened: { label: `Opened`, color: `amber` },
  completed: { label: `Completed`, color: `green` },
  expired: { label: `Expired`, color: `gray` },
  cancelled: { label: `Cancelled`, color: `gray` },
}

/** What happened to a finished form on its way into the patient chart. */
const CHART_TEXT: Record<string, string> = {
  SUCCESS: `In the patient chart`,
  PENDING: `Filing in the patient chart`,
  FAILED: `Did not reach the patient chart`,
  NOT_SUPPORTED: `This practice system does not take chart documents`,
}

const WHEN = `MMM D, h:mm A`

function PacketRow({
  record,
  forPatient,
  now,
  busy,
  onCopy,
  onNewLink,
  onCancel,
}: {
  record: PaperworkRecord
  /** Set only when the contact has several patient records, so staff can tell whose packet this is. */
  forPatient?: PatientChoice
  now: Date
  busy: boolean
  onCopy: () => void
  onNewLink: () => void
  onCancel: () => void
}) {
  const state = paperworkState(record, now)
  const packet = parseSentPacket(record.snapshotJson)
  const answers = parseSubmittedAnswers(record.submissionsJson)
  const live = state === `sent` || state === `opened`
  const chartFailed = record.processingStatus === `FAILED` || record.processingStatus === `PARTIAL_FAILURE`
  const events = [
    `Sent ${moment(record.createdDate).format(WHEN)}`,
    record.openedDate && `Opened ${moment(record.openedDate).format(WHEN)}`,
    record.completedDate && `Completed ${moment(record.completedDate).format(WHEN)}`,
    record.cancelledDate && `Cancelled ${moment(record.cancelledDate).format(WHEN)}`,
    live && `Link expires ${moment(record.expiresDate).format(`MMM D`)}`,
    state === `expired` && `Expired ${moment(record.expiresDate).format(`MMM D`)}`,
  ].filter(Boolean)

  const openAnswers = () =>
    showModal(<PaperworkAnswers packet={packet} answers={answers} />, {
      useRadix: true,
      isFullScreen: false,
      hideTitle: true,
      removePadding: true,
      panelClassName: `min-w-0 sm:max-w-xl`,
    })

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-default bg-surface p-4 shadow-elevation-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-strong">{packet.name || `Paperwork`}</span>
        <Badge {...STATE_BADGES[state]} />
      </div>
      {forPatient && (
        <p className="text-sm text-default">
          {`For ${patientName(forPatient)}`}
          <span className="text-subtle">{` · ${patientDetails(forPatient)}`}</span>
        </p>
      )}
      <p className="text-xs text-subtle">{events.join(` · `)}</p>

      {chartFailed && (
        <p className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          <WarningTriangle className="size-4 shrink-0" />
          Some forms did not reach the patient chart.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {record.formStatuses.map((form) => {
          const chart = record.chartWriteStatuses.find((write) => write.formId === form.formId)
          return (
            <li key={form.formId} className="flex items-start gap-2 text-sm">
              {form.completed ? (
                <CheckCircle className="mt-0.5 size-4 shrink-0 text-success-default" />
              ) : (
                <Circle className="mt-0.5 size-4 shrink-0 text-minimal" />
              )}
              <span className="flex min-w-0 flex-col">
                <span className="text-strong">{form.name}</span>
                <span className="text-xs text-subtle">
                  {form.completed && form.submittedDate
                    ? [
                        `Finished ${moment(form.submittedDate).format(WHEN)}`,
                        state === `completed` && chart && CHART_TEXT[chart.status],
                      ]
                        .filter(Boolean)
                        .join(` · `)
                    : `Not finished`}
                </span>
                {chart?.status === `FAILED` && chart.errorMessage && (
                  <span className="text-xs text-red-600">{chart.errorMessage}</span>
                )}
              </span>
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap gap-2">
        {Object.keys(answers).length > 0 && (
          <Button variant="white" size="xs" onClick={openAnswers}>
            View answers
          </Button>
        )}
        {live && (
          <Button variant="white" size="xs" onClick={onCopy}>
            Copy link
          </Button>
        )}
        {state !== `completed` && (
          <Button variant="white" size="xs" disabled={busy} onClick={onNewLink}>
            New link
          </Button>
        )}
        {live && (
          <Button variant="subtle" size="xs" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </li>
  )
}

export function DocumentsTab({
  records,
  fetching,
  failed,
  hasPatientLink,
  patients,
  busyId,
  copyLink,
  newLink,
  cancel,
  now: fixedNow,
}: UseContactDocumentsReturn & { now?: Date }) {
  const now = fixedNow ?? new Date()
  const openSend = () =>
    showModal(<SendPaperworkDialog patients={patients} onClose={closeModal} />, {
      useRadix: true,
      isFullScreen: false,
      hideTitle: true,
      removePadding: true,
      overflowHidden: true,
      panelClassName: `min-w-0 sm:max-w-lg`,
    })
  const sendButton = (
    <Button variant="primary" size="sm" disabled={patients.length === 0} onClick={openSend}>
      Send paperwork
    </Button>
  )

  if (!hasPatientLink) {
    return (
      <TableEmptyState
        icon={<Page className="h-6 w-6" strokeWidth={1.5} />}
        title="No patient record yet"
        description="Paperwork goes to a patient's chart, so it can be sent once this contact is linked to a patient in the practice system."
      />
    )
  }
  if (fetching) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={20} />
      </div>
    )
  }
  if (failed) {
    return <TableEmptyState title="Couldn't load paperwork" description="Try again in a moment." />
  }
  if (records.length === 0) {
    return (
      <TableEmptyState
        icon={<Page className="h-6 w-6" strokeWidth={1.5} />}
        title="No paperwork sent"
        description="Paperwork sent to this patient shows here with what they finished and whether it reached their chart."
        action={sendButton}
      />
    )
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
      <div className="flex justify-end">{sendButton}</div>
      <ul className="flex flex-col gap-3">
        {records.map((record) => (
          <PacketRow
            key={record.id}
            record={record}
            forPatient={patients.length > 1 ? patients.find((patient) => patient.id === record.patientId) : undefined}
            now={now}
            busy={busyId === record.id}
            onCopy={() => copyLink(record.link)}
            onNewLink={() => newLink(record)}
            onCancel={() => cancel(record)}
          />
        ))}
      </ul>
    </div>
  )
}
